import { IconCornerDownRight } from "@tabler/icons-react";
import { getIn, useFormikContext } from "formik";

// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import { ReactSelect } from "../UI/Select";
import Text from "../UI/Text";
import Status from "../UI/Status";
import {
  DETAILS_TOLERANCE,
  ENERGY_OPTIONS,
} from "../../utils/constants/balances";
import type { BalanceTypeDefinition } from "../../models/balance";
import styles from "./BalanceDetailsFields.module.css";

interface BalanceDetailsFieldsProps {
  prefix?: string;
  balanceType?: BalanceTypeDefinition;
  disabled?: boolean;
}

/** Nature de l'énergie consommée, pour les types qui la demandent. */
export function BalanceEnergyField({
  prefix = "",
  balanceType,
  disabled = false,
}: BalanceDetailsFieldsProps) {
  if (!balanceType?.has_energy) return null;

  return (
    <ReactSelect
      label="Énergie"
      placeholder="Électricité"
      name={`${prefix}details.energy`}
      options={ENERGY_OPTIONS}
      isDisabled={disabled}
    />
  );
}

function toAmount(value: unknown): number {
  const amount = Number(value);
  return Number.isNaN(amount) ? 0 : Math.abs(amount);
}

/** Sous-montants « Dont … » que le type ouvre : facultatifs, dans la limite du montant. */
export function BalanceAmountDetailsFields({
  prefix = "",
  balanceType,
  disabled = false,
}: BalanceDetailsFieldsProps) {
  const { values } = useFormikContext<Record<string, unknown>>();
  const details = balanceType?.details ?? [];
  if (details.length === 0) return null;

  const montant = toAmount(getIn(values, `${prefix}montant`));
  const filled = details.filter(
    (detail) => getIn(values, `${prefix}details.${detail.key}`) !== "",
  );
  const total = filled.reduce(
    (sum, detail) =>
      sum + toAmount(getIn(values, `${prefix}details.${detail.key}`)),
    0,
  );
  const gap = montant - total;

  // Le détail est facultatif : un reste non ventilé est signalé, un dépassement est une
  // erreur (le schéma bloque la validation).
  let balanceMessage = null;
  if (filled.length > 0 && gap < -DETAILS_TOLERANCE) {
    balanceMessage = (
      <Text color="danger">
        Le détail dépasse le montant de {currencyFormatter(-gap)}.
      </Text>
    );
  } else if (filled.length > 0 && gap > DETAILS_TOLERANCE) {
    balanceMessage = (
      <Text color="muted">
        Il reste {currencyFormatter(gap)} non ventilés sur{" "}
        {currencyFormatter(montant)}.
      </Text>
    );
  }

  return (
    <div className={styles.block}>
      <div className={styles.header}>
        <Text weight="bold">Détail du montant</Text>
        <Status color="neutral" text="Facultatif" as="span" />
      </div>
      {details.map((detail) => {
        const name = `${prefix}details.${detail.key}`;
        return (
          <div key={detail.key} className={styles.row}>
            <IconCornerDownRight size={16} className={styles.icon} />
            <Text as="label" htmlFor={name}>
              {detail.label}
            </Text>
            <TextInput
              type="number"
              step="0.01"
              id={name}
              name={name}
              placeholder="0,00"
              unit="€"
              size="sm"
              marginForm=""
              disabled={disabled}
            />
          </div>
        );
      })}
      {balanceMessage}
    </div>
  );
}
